/**
 * Canonical audit taxonomy for auth and security-sensitive flows (API-1236).
 */
export enum AuditEventType {
  REGISTER = 'REGISTER',
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILURE = 'LOGIN_FAILURE',
  LOGOUT = 'LOGOUT',
  TOKEN_REFRESH = 'TOKEN_REFRESH',
  TOKEN_REUSE_DETECTED = 'TOKEN_REUSE_DETECTED',
  PASSWORD_RESET_REQUESTED = 'PASSWORD_RESET_REQUESTED',
  PASSWORD_RESET_COMPLETED = 'PASSWORD_RESET_COMPLETED',
  EMAIL_VERIFIED = 'EMAIL_VERIFIED',
  /** Verification email re-sent to an unverified account (resend-verification). */
  VERIFICATION_RESENT = 'VERIFICATION_RESENT',
  EMAIL_CHANGED = 'EMAIL_CHANGED',
  /** User requested a pending email change; verification sent to new address. */
  EMAIL_CHANGE_REQUESTED = 'EMAIL_CHANGE_REQUESTED',
  /** Authenticated user changed password (sessions may be revoked). */
  PASSWORD_CHANGED = 'PASSWORD_CHANGED',
  /** OAuth authorization code exchanged and session established (existing or new user). */
  OAUTH_LOGIN_SUCCESS = 'OAUTH_LOGIN_SUCCESS',
  /** New user created through OAuth sign-in (no prior LOCI account). */
  OAUTH_REGISTER = 'OAUTH_REGISTER',
  PROVIDER_LINKED = 'PROVIDER_LINKED',
  PROVIDER_UNLINKED = 'PROVIDER_UNLINKED',
  SESSION_REVOKED = 'SESSION_REVOKED',
  SESSION_REVOKED_ALL = 'SESSION_REVOKED_ALL',
  MAGIC_LINK_REQUESTED = 'MAGIC_LINK_REQUESTED',
  MAGIC_LINK_CONSUMED = 'MAGIC_LINK_CONSUMED',
  MAGIC_LINK_EXPIRED = 'MAGIC_LINK_EXPIRED',
  MAGIC_LINK_ALREADY_USED = 'MAGIC_LINK_ALREADY_USED',
  /**
   * hello@ account recovery (API-1810). An admin asked
   * `POST /api/v1/admin/account-recovery/links` for a single-use magic link to
   * a new address. `success: true` = issued; `success: false` carries a
   * `reason` (`not_found`, `agent_account`, `self_recovery`,
   * `super_admin_target`, `placeholder_email`, `same_email`, `email_taken`,
   * `send_failed`, `internal_error`). `userId` is the target; `metadata`
   * carries `issuerId`, `oldEmailHash`, `newEmailHash`, `evidenceType`, and
   * `recoveryLinkId` when a row was written. Emails are never logged raw.
   */
  ACCOUNT_RECOVERY_LINK_ISSUED = 'ACCOUNT_RECOVERY_LINK_ISSUED',
  /**
   * A recovery link was consumed (API-1810). `success: true` = the account
   * moved to the new address, marked verified; `success: false` carries a
   * `reason` (`email_taken`, `agent_account`, `user_missing`). Same hashed
   * `metadata` shape as {@link AuditEventType.ACCOUNT_RECOVERY_LINK_ISSUED}.
   */
  ACCOUNT_RECOVERY_CONSUMED = 'ACCOUNT_RECOVERY_CONSUMED',
  ADMIN_ACTION = 'ADMIN_ACTION',
  ROLE_CHANGED = 'ROLE_CHANGED',
  /**
   * Self-service account deletion (`DELETE /api/v1/auth/account`, API-1830).
   * `success: true` = the 30-day deletion was scheduled; `success: false`
   * carries a `reason` (`cannot_delete_last_super_admin`, `serialization_conflict`,
   * `internal_error`). `metadata.superAdmin` is whether the DB role (not the
   * JWT claim) was SUPER_ADMIN at decision time.
   */
  ACCOUNT_DELETION_REQUESTED = 'ACCOUNT_DELETION_REQUESTED',
  /** Emitted on every successful auth-gate pass (token validation). Not a login event. */
  REQUEST_AUTHENTICATED = 'REQUEST_AUTHENTICATED',
  /**
   * Refine admin CRUD audits (M2 events + M3 venues).
   *
   * `EVENT_*` events are emitted by both the events CRUD endpoints (API-1468..71)
   * and by the venue DELETE cascade — when an admin force-deletes a venue, each
   * linked event also emits an `EVENT_DELETED` audit whose `metadata.reason`
   * carries the originating venue id. For event CRUD specifically, `metadata.eventId`
   * is always present; `EVENT_UPDATED` additionally carries `metadata.diff`
   * (per-field from/to map produced by `computeShallowDiff`).
   */
  EVENT_CREATED = 'EVENT_CREATED',
  EVENT_UPDATED = 'EVENT_UPDATED',
  EVENT_DELETED = 'EVENT_DELETED',
  VENUE_CREATED = 'VENUE_CREATED',
  VENUE_UPDATED = 'VENUE_UPDATED',
  VENUE_DELETED = 'VENUE_DELETED',
  /**
   * PIP-408 — venue moratorium release. `metadata` carries `candidateId`,
   * `locationId`, and the attached / unpinned / re-scored event counts.
   */
  VENUE_CANDIDATE_PROMOTED = 'VENUE_CANDIDATE_PROMOTED',
  VENUE_CANDIDATE_ATTACHED = 'VENUE_CANDIDATE_ATTACHED',
  /**
   * Refine admin media upload (API-1478).
   *
   * Emitted by `POST /api/v1/admin/media/upload` after a successful image
   * upload. `metadata` carries the resulting `url`, `sizeBytes` (the bytes
   * the server received before optimization), and `mimeType` (the source
   * MIME the client sent). Failures (Zod / oversize / bad MIME) emit
   * `success: false` with a `reason` string (`missing_file`, `oversize`,
   * `bad_mime`, `internal_error`).
   */
  MEDIA_UPLOADED = 'MEDIA_UPLOADED',
}

/** Matches Prisma `ClientType` / auth routes. */
export type ClientType = 'WEB' | 'MOBILE' | 'API';

/**
 * Structured audit payload emitted under the SECURITY_AUDIT log channel.
 */
export interface AuditEvent {
  type: AuditEventType;
  timestamp: string;
  userId?: string;
  sessionId?: string;
  correlationId: string;
  clientType: ClientType;
  /** SHA-256 hex of `${IP_HASH_SALT}:${rawIp}` (empty string when IP is unknown). */
  ip: string;
  userAgent: string;
  metadata: Record<string, unknown>;
  success: boolean;
  reason?: string;
}
